import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LandingPage } from "@/pages/landing-page";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/ui/sonner";
import { ScrollToTop } from "@/components/common/scroll-to-top";

// Eagerly load entry points + protect/layout shells (small).
// Lazy-load everything else so the landing bundle stays minimal.
const NotFoundPage = lazy(() =>
  import("@/pages/not-found-page").then((m) => ({ default: m.NotFoundPage })),
);
const LoginPage = lazy(() =>
  import("@/pages/auth/login-page").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("@/pages/auth/register-page").then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/auth/forgot-password-page").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ChangePasswordPage = lazy(() =>
  import("@/pages/auth/change-password-page").then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
const ContactPage = lazy(() =>
  import("@/pages/contact-page").then((m) => ({ default: m.ContactPage })),
);
const AboutPage = lazy(() =>
  import("@/pages/about-page").then((m) => ({ default: m.AboutPage })),
);
const PricingPage = lazy(() =>
  import("@/pages/pricing-page").then((m) => ({ default: m.PricingPage })),
);
const BlogListPage = lazy(() =>
  import("@/pages/blog-list-page").then((m) => ({ default: m.BlogListPage })),
);
const BlogPostPage = lazy(() =>
  import("@/pages/blog-post-page").then((m) => ({ default: m.BlogPostPage })),
);
const ProtectedRoute = lazy(() =>
  import("@/components/auth/protected-route").then((m) => ({ default: m.ProtectedRoute })),
);
const OwnerRoute = lazy(() =>
  import("@/components/auth/owner-route").then((m) => ({ default: m.OwnerRoute })),
);
const AppLayout = lazy(() =>
  import("@/components/layout/app-layout").then((m) => ({ default: m.AppLayout })),
);
const DashboardPage = lazy(() =>
  import("@/pages/dashboard-page").then((m) => ({ default: m.DashboardPage })),
);
const CustomersPage = lazy(() =>
  import("@/pages/customers-page").then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazy(() =>
  import("@/pages/customer-detail-page").then((m) => ({ default: m.CustomerDetailPage })),
);
const VehiclesPage = lazy(() =>
  import("@/pages/vehicles-page").then((m) => ({ default: m.VehiclesPage })),
);
const VehicleDetailPage = lazy(() =>
  import("@/pages/vehicle-detail-page").then((m) => ({ default: m.VehicleDetailPage })),
);
const ServiceRecordsPage = lazy(() =>
  import("@/pages/service-records-page").then((m) => ({ default: m.ServiceRecordsPage })),
);
const ServiceRecordDetailPage = lazy(() =>
  import("@/pages/service-record-detail-page").then((m) => ({ default: m.ServiceRecordDetailPage })),
);
const PartSuppliesPage = lazy(() =>
  import("@/pages/part-supplies-page").then((m) => ({ default: m.PartSuppliesPage })),
);
const PartSupplyDetailPage = lazy(() =>
  import("@/pages/part-supply-detail-page").then((m) => ({ default: m.PartSupplyDetailPage })),
);
const SuppliersPage = lazy(() =>
  import("@/pages/suppliers-page").then((m) => ({ default: m.SuppliersPage })),
);
const SupplierDetailPage = lazy(() =>
  import("@/pages/supplier-detail-page").then((m) => ({ default: m.SupplierDetailPage })),
);
const FinancePage = lazy(() =>
  import("@/pages/finance-page").then((m) => ({ default: m.FinancePage })),
);
const EmployeesPage = lazy(() =>
  import("@/pages/employees-page").then((m) => ({ default: m.EmployeesPage })),
);
const EmployeeDetailPage = lazy(() =>
  import("@/pages/employee-detail-page").then((m) => ({ default: m.EmployeeDetailPage })),
);
const ShopPage = lazy(() =>
  import("@/pages/shop-page").then((m) => ({ default: m.ShopPage })),
);
const BranchesPage = lazy(() =>
  import("@/pages/branches-page").then((m) => ({ default: m.BranchesPage })),
);
const AuditLogsPage = lazy(() =>
  import("@/pages/audit-logs-page").then((m) => ({ default: m.AuditLogsPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/hakkimizda" element={<AboutPage />} />
          <Route path="/fiyatlandirma" element={<PricingPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          <Route element={<ProtectedRoute />}>
            <Route path="change-password" element={<ChangePasswordPage />} />
            <Route element={<AppLayout />}>
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              <Route path="vehicles" element={<VehiclesPage />} />
              <Route path="vehicles/:id" element={<VehicleDetailPage />} />
              <Route path="service-records" element={<ServiceRecordsPage />} />
              <Route
                path="service-records/:id"
                element={<ServiceRecordDetailPage />}
              />
              <Route path="part-supplies" element={<PartSuppliesPage />} />
              <Route
                path="part-supplies/:id"
                element={<PartSupplyDetailPage />}
              />
              <Route path="suppliers" element={<SuppliersPage />} />
              <Route path="suppliers/:id" element={<SupplierDetailPage />} />

              {/* Owner-only sayfalar */}
              <Route element={<OwnerRoute />}>
                <Route path="finance" element={<FinancePage />} />
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="employees/:id" element={<EmployeeDetailPage />} />
                <Route path="branches" element={<BranchesPage />} />
                <Route path="audit-logs" element={<AuditLogsPage />} />
                <Route path="shop" element={<ShopPage />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
